import { createFileRoute } from "@tanstack/react-router";
import { PolicyLayout, PolicySection } from "@/components/skl/PolicyLayout";

export const Route = createFileRoute("/privatlivspolitik")({
  head: () => ({
    meta: [
      { title: "Privatlivspolitik — SKL-Flow" },
      { name: "description", content: "Sådan behandler SKL-Flow personoplysninger i henhold til GDPR og dansk databeskyttelseslovgivning." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Privatlivspolitik — SKL-Flow" },
      { property: "og:description", content: "Information om SKL-Flows behandling af personoplysninger, dine rettigheder og kontakt." },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://sklflow.com/privatlivspolitik" }],
  }),
  component: Privatlivspolitik,
});

function Privatlivspolitik() {
  return (
    <PolicyLayout title="Privatlivspolitik" updated="5. juli 2026">
      <PolicySection title="1. Dataansvarlig">
        <p>
          SKL-Flow er dataansvarlig for behandlingen af de personoplysninger, som vi modtager om dig.
          Du kan kontakte os på:
          <br />
          E-mail: emilsklanderandersen@gmail.com
          <br />
          Web: sklflow.com
        </p>
      </PolicySection>

      <PolicySection title="2. Hvilke personoplysninger behandler vi">
        <p>Vi behandler følgende kategorier af personoplysninger:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Kontaktoplysninger: navn, e-mail, telefonnummer og virksomhedsnavn.</li>
          <li>Oplysninger, du selv oplyser i kontaktformularer eller korrespondance.</li>
          <li>Brugerkontooplysninger: e-mail, hashed adgangskode og tilknyttede profiloplysninger.</li>
          <li>Tekniske oplysninger: IP-adresse, browsertype, besøgstidspunkt og enhedsdata.</li>
          <li>Kommunikationshistorik i forbindelse med support og salg.</li>
        </ul>
      </PolicySection>

      <PolicySection title="3. Formål og retsgrundlag">
        <p>Vi behandler dine oplysninger til følgende formål:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Levering af vores digitale ydelser og opfyldelse af aftale — retsgrundlag: databeskyttelses­forordningens
            (GDPR) art. 6, stk. 1, litra b.
          </li>
          <li>
            Besvarelse af henvendelser via kontaktformular — retsgrundlag: GDPR art. 6, stk. 1, litra b
            eller f (legitim interesse i at besvare henvendelser).
          </li>
          <li>
            Oprettelse og administration af brugerkonto — retsgrundlag: GDPR art. 6, stk. 1, litra b.
          </li>
          <li>
            Overholdelse af retlige forpligtelser, herunder bogføringsloven — retsgrundlag: GDPR art. 6,
            stk. 1, litra c.
          </li>
          <li>
            Forbedring af tjenesten samt sikring mod misbrug — retsgrundlag: GDPR art. 6, stk. 1, litra f.
          </li>
        </ul>
      </PolicySection>

      <PolicySection title="4. Modtagere og databehandlere">
        <p>
          Vi videregiver kun personoplysninger, når det er nødvendigt for at levere vores ydelser eller
          for at overholde lovgivningen. Vi anvender bl.a. følgende typer databehandlere:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Hosting- og infrastrukturudbydere (fx Cloudflare og Supabase) inden for EU/EØS eller under gyldige overførselsgrundlag.</li>
          <li>Udbydere af e-mailtjenester til udsendelse af transaktions- og bekræftelsesmails.</li>
          <li>Analyse- og fejlrapporteringsværktøjer i det omfang, det er nødvendigt for driften.</li>
        </ul>
        <p>
          Der er indgået databehandleraftaler med samtlige databehandlere i overensstemmelse med
          GDPR art. 28. Ved overførsel til tredjelande sikres et passende beskyttelsesniveau, fx via
          EU-Kommissionens standardkontraktbestemmelser (SCC).
        </p>
      </PolicySection>

      <PolicySection title="5. Opbevaringsperiode">
        <p>
          Vi opbevarer personoplysninger, så længe det er nødvendigt for det formål, oplysningerne er
          indsamlet til:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Henvendelser via kontaktformular: op til 24 måneder efter sidste kontakt.</li>
          <li>Brugerkonti: så længe kontoen er aktiv. Slettes ved sletning af konto.</li>
          <li>Regnskabsmateriale: 5 år fra udløbet af det regnskabsår, materialet vedrører, jf. bogføringsloven.</li>
        </ul>
      </PolicySection>

      <PolicySection title="6. Dine rettigheder">
        <p>Du har efter databeskyttelsesforordningen ret til:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Indsigt i, hvilke oplysninger vi behandler om dig.</li>
          <li>Berigtigelse af urigtige oplysninger.</li>
          <li>Sletning af oplysninger (“retten til at blive glemt”), i det omfang de ikke er nødvendige at bevare.</li>
          <li>Begrænsning af behandlingen.</li>
          <li>Dataportabilitet — udlevering af dine oplysninger i et almindeligt anvendt format.</li>
          <li>Indsigelse mod behandling, der baseres på legitim interesse.</li>
          <li>Tilbagekaldelse af samtykke, hvor behandlingen sker på grundlag af samtykke.</li>
        </ul>
        <p>
          Anmodninger kan rettes til emilsklanderandersen@gmail.com. Vi besvarer henvendelsen inden for én måned.
        </p>
      </PolicySection>

      <PolicySection title="7. Sikkerhed">
        <p>
          Vi træffer passende tekniske og organisatoriske foranstaltninger for at beskytte
          personoplysninger mod uautoriseret adgang, tab eller ændring, herunder kryptering under
          transport (TLS), adgangsstyring og løbende opdatering af systemer.
        </p>
      </PolicySection>

      <PolicySection title="8. Klage">
        <p>
          Du har ret til at klage til Datatilsynet over vores behandling af dine personoplysninger.
          Datatilsynet, Carl Jacobsens Vej 35, 2500 Valby, telefon 33 19 32 00, e-mail dt@datatilsynet.dk,
          web{" "}
          <a href="https://www.datatilsynet.dk" className="underline hover:no-underline">
            www.datatilsynet.dk
          </a>
          .
        </p>
      </PolicySection>

      <PolicySection title="9. Ændringer">
        <p>
          Denne privatlivspolitik kan opdateres løbende. Den til enhver tid gældende version er
          tilgængelig på sklflow.com/privatlivspolitik.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
